import { create } from "zustand";

/**
 * A tenant-wide access block the backend can signal on any request:
 * - `SUBSCRIPTION_EXPIRED`: the company's subscription lapsed (HTTP 402, errorCode 40). The user stays
 *   signed in and is funnelled to the renewal screen (Netflix-style); payment/subscription stay reachable.
 * - `COMPANY_OFFBOARDED`: the company was deactivated (HTTP 403, errorCode 39). The user is locked out and
 *   sent to the "company deactivated" screen; paying does not undo it.
 */
export type AccessBlock = "SUBSCRIPTION_EXPIRED" | "COMPANY_OFFBOARDED";

interface AccessBlockState {
  /** The active block, or null when access is normal. */
  block: AccessBlock | null;
  /** Raise a block (set by the HTTP error interceptor when the backend signals it). */
  setBlock: (block: AccessBlock) => void;
  /** Clear the block (renewed subscription, fresh login, logout, or "back to login"). */
  clear: () => void;
}

/**
 * In-memory only (never persisted): a fresh page load (e.g. returning from the external Iyzico
 * checkout) starts unblocked, and the next business call re-signals a 402/403 if the state persists.
 */
const useAccessBlockStore = create<AccessBlockState>((set) => ({
  block: null,
  setBlock: (block) => set({ block }),
  clear: () => set({ block: null }),
}));

export default useAccessBlockStore;
